import { useState } from "react";
import { Grid, Box, TextField, InputAdornment } from "@mui/material";
import { useMenuLinkNavigation } from "../../../../hooks/useMenuLinkNavigation";
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import SearchIcon from '@mui/icons-material/Search';
import { IMenuResponse } from "../../../../../model/base/response/menu-response";
import { IMenuItemResponse } from "../../../../../model/base/response/menu-item-reponse";
import {
  MegaMenuPaper,
  MenuColumn,
  MenuTitle,
  MenuItemsList,
  MenuItemLink,
  EmptyState,
} from "./megaMenu.styles";

interface MegaMenuProps {
  menus: IMenuResponse[];
  onItemClick: () => void;
}

export function MegaMenuComponent({ menus, onItemClick }: MegaMenuProps) {
  const openMenuLink = useMenuLinkNavigation();
  const [searchTerm, setSearchTerm] = useState('');

  // Filtrar menus e items baseado no termo de busca
  const filterMenusAndItems = () => {
    if (!searchTerm.trim()) {
      // Sem busca, retorna todos os menus ordenados
      return [...menus].sort((a, b) => (a.orderNumber || 0) - (b.orderNumber || 0));
    }

    const lowerSearch = searchTerm.toLowerCase().trim();

    return menus
      .map(menu => {
        // Verifica se o nome do menu corresponde
        const menuMatches = menu.name?.toLowerCase().includes(lowerSearch);

        // Filtra items que correspondem à busca ou se o menu corresponde (mostra todos os items)
        const filteredItems = menu.items
          ?.filter(item => item.isPublished !== false)
          ?.filter(item => {
            if (menuMatches) return true; // Se menu match, mostra todos items
            return item.name?.toLowerCase().includes(lowerSearch);
          })
          ?.sort((a, b) => (a.orderNumber || 0) - (b.orderNumber || 0)) || [];

        return {
          ...menu,
          items: filteredItems,
          _hasMatch: menuMatches || filteredItems.length > 0
        };
      })
      .filter(menu => menu._hasMatch) // Remove menus sem correspondência
      .sort((a, b) => (a.orderNumber || 0) - (b.orderNumber || 0));
  };

  const filteredMenus = filterMenusAndItems();

  // Filtrar e ordenar items de cada menu
  const getMenuItems = (menu: IMenuResponse): IMenuItemResponse[] => {
    if (!menu.items || menu.items.length === 0) return [];

    return menu.items
      .filter((item) => item.isPublished !== false) // Apenas items publicados
      .sort((a, b) => (a.orderNumber || 0) - (b.orderNumber || 0));
  };

  const handleItemClick = (menuItem: IMenuItemResponse) => {
    if (!menuItem.url) {
      console.warn('Menu item sem URL configurada:', menuItem);
      return;
    }

    // Externa (indicado pelo backend) abre em nova aba; interna (ex.: "pagina/{slug}") preserva o prefixo /:state/:city
    openMenuLink(menuItem.url, menuItem.isExternal);
    onItemClick();
  };

  if (menus.length === 0) {
    return (
      <MegaMenuPaper>
        <EmptyState>Nenhum menu disponível no momento.</EmptyState>
      </MegaMenuPaper>
    );
  }

  return (
    <MegaMenuPaper elevation={6}>
      {/* Campo de busca no topo direito */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
        <TextField
          size="small"
          placeholder="Digite para buscar..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: 'text.secondary' }} />
              </InputAdornment>
            ),
          }}
          sx={{
            width: { xs: '100%', sm: '300px' },
            backgroundColor: 'white',
            borderRadius: 1,
            '& .MuiOutlinedInput-root': {
              '&:hover fieldset': {
                borderColor: 'primary.main',
              },
            },
          }}
        />
      </Box>

      {/* Resultados filtrados */}
      {filteredMenus.length === 0 ? (
        <EmptyState>
          Nenhum resultado encontrado para "{searchTerm}".
        </EmptyState>
      ) : (
        <Grid container spacing={2} sx={{ width: '100%' }}>
          {filteredMenus.map((menu) => {
            const menuItems = getMenuItems(menu);

            // Menu de link direto (URL ou página customizada) não tem itens: o título leva ao destino
            const isDirectLink = !!menu.url;
            if (menuItems.length === 0 && !isDirectLink) return null;

            const openMenu = () => {
              openMenuLink(menu.url, menu.isExternal);
              onItemClick();
            };

            return (
              <Grid
                item
                xs={12}
                sm={6}
                md={4}
                lg={3}
                key={menu.id}
                sx={{
                  display: 'flex',
                  flexDirection: 'column'
                }}
              >
                <MenuColumn>
                  <MenuTitle
                    {...(isDirectLink && {
                      onClick: openMenu,
                      role: "button",
                      tabIndex: 0,
                      style: { cursor: "pointer" },
                      onKeyDown: (e: React.KeyboardEvent) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          openMenu();
                        }
                      },
                    })}
                  >
                    {menu.name}
                    {isDirectLink && menu.isExternal && (
                      <OpenInNewIcon sx={{ fontSize: 16, ml: 0.5 }} />
                    )}
                  </MenuTitle>
                  {isDirectLink && menu.description && (
                    <Box component="p" sx={{ m: 0, color: "text.secondary", fontSize: "0.875rem" }}>
                      {menu.description}
                    </Box>
                  )}
                  <MenuItemsList>
                    {menuItems.map((item) => (
                      <MenuItemLink
                        key={item.id}
                        onClick={() => handleItemClick(item)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            handleItemClick(item);
                          }
                        }}
                        title={item.isExternal ? `Abre em nova aba: ${item.url}` : item.url}
                      >
                        <span>{item.name}</span>
                        {item.isExternal && (
                          <OpenInNewIcon sx={{ fontSize: 16, ml: 0.5 }} />
                        )}
                      </MenuItemLink>
                    ))}
                  </MenuItemsList>
                </MenuColumn>
              </Grid>
            );
          })}
        </Grid>
      )}
    </MegaMenuPaper>
  );
}
